import { Children, isValidElement, useState } from "react"
import type { ReactNode } from "react"
import { TrendingDown, TrendingUp } from "lucide-react"

import { MetricDetailDialog, type DetalleDeCifra } from "@/components/MetricDetailDialog"
import { Sparkline } from "@/components/charts/Sparkline"
import { Badge } from "@/components/ui/badge"
import { formatAmount, formatPercent } from "@/lib/format"
import { cn } from "@/lib/utils"
import { t } from "@/lib/i18n"

/**
 * Cifra de portada. Una sola implementacion para las cuatro pantallas.
 *
 * Habia tres versiones casi iguales —`Cifra`, `Resumen`, `Metrica`— en P&L,
 * caja, desglose y dashboard. Tres copias divergen: alguien alinea los decimales
 * en una y las otras dos se quedan como estaban, y el mismo indicador se ve
 * distinto segun por donde se entre.
 *
 * DECISIONES DE DISENO, siguiendo el sistema de `apple-ui-design` pero SIN
 * romper una herramienta densa:
 *
 * - CLARIDAD: el numero manda. Es lo mas grande de la tarjeta, con `tracking`
 *   cerrado y cifras tabulares para que las columnas no bailen al actualizarse.
 * - DEFERENCIA: la etiqueta y el contexto van en gris, pequenos y en mayusculas
 *   discretas. El marco no compite con el dato.
 * - PROFUNDIDAD: una sombra minima despega la fila de metricas de las tablas que
 *   vienen debajo. Es la unica capa elevada de la pantalla; si todo flota, nada
 *   destaca.
 *
 * Lo que NO se tomo de ese sistema, a proposito: el ancho maximo de 680px, el
 * cuerpo de 17px y los espaciados de 96px. Estan pensados para paginas de
 * lectura, y aca una tabla de P&L tiene doce columnas de meses mas totales: con
 * esas medidas no entra en pantalla y obliga a desplazarse para comparar dos
 * meses, que es exactamente lo que se viene a hacer.
 *
 * ---------------------------------------------------------------------------
 * LA CIFRA VA EN LIGHT, Y EL ROJO ES UNO POR VISTA
 *
 * Lamina 18, literal: **«Cifras: Light o Regular en tamano grande, tabulares.
 * Un solo dato en rojo por vista.»** Esta tarjeta hacia las dos cosas al reves:
 * la cifra iba en SemiBold —el peso del titulo, asi que la cifra competia con
 * el titular en vez de ser el dato— y **pintaba de rojo CUALQUIER negativo**.
 *
 * Lo segundo es lo que de verdad rompia la pantalla. En el dashboard hay siete
 * tarjetas; con tres negativas salian tres rojos, y el rojo que senala una cosa
 * deja de senalar cuando senala tres. El brandbook lo dice al reves de como
 * estaba escrito: el rojo no es «negativo», es **«la que requiere atencion»**, y
 * eso lo decide la pantalla, no el signo.
 *
 * Asi que el rojo sale de `tono="alerta"` y de ningun otro sitio. Un negativo
 * se lee por el signo menos y por la alineacion tabular, que es para lo que
 * existen — y el brandbook es explicito: «los errores se comunican con texto e
 * icono, nunca solo con color».
 *
 * `MetricRow` tiene un guardian en DESARROLLO que avisa si una fila declara dos
 * `alerta`. No se puede resolver solo —cual de las dos es LA que importa es una
 * decision de producto, no de un componente— pero callarselo es como no tener
 * la regla.
 */
export type MetricTono = "neutro" | "destacado" | "alerta"

interface MetricCardProps {
  label: string
  /** Importe. `null` cuando la cifra no es numerica: se muestra `badge`. */
  value: number | null
  /** Se muestra en vez del importe cuando `value` es `null`. */
  badge?: ReactNode
  /** Variacion contra el periodo anterior, como fraccion (-0.28 = -28%). */
  variation?: number | null
  /** Denominador para el "% sobre ingresos". */
  share?: number
  /** Rotulo del share. Por defecto, "sobre ingresos". */
  shareLabel?: string
  loading?: boolean
  tono?: MetricTono
  /** Recuentos, no dinero: sin separador de miles ni decimales. */
  integer?: boolean
  /**
   * Invierte el color de la variacion. Que un GASTO suba no es buena noticia
   * aunque la flecha apunte hacia arriba: el color tiene que decir bien/mal, no
   * arriba/abajo.
   */
  invertTrend?: boolean
  /**
   * LA SERIE DETRÁS DE LA CIFRA. Con ella la tarjeta dibuja una miniatura y se
   * vuelve un botón: al pulsarla se abre el detalle período a período en una
   * ventana (`MetricDetailDialog`). Sin ella, es la cifra de siempre.
   *
   * Cesar, 2026-10-04: «metiendo más pop ups y diseño top». Ver la nota del
   * diálogo para por qué es sólo lectura.
   */
  detalle?: DetalleDeCifra
}

export function MetricCard({
  label, value, badge, variation, share, shareLabel = "sobre ingresos",
  loading, tono = "neutro", integer, invertTrend, detalle,
}: MetricCardProps) {
  const sube = (variation ?? 0) > 0
  const buenaNoticia = invertTrend ? !sube : sube
  const [abierto, setAbierto] = useState(false)
  const abrible = Boolean(detalle && !loading && value !== null)
  // Un botón de verdad cuando se puede abrir: foco, Enter y lector de pantalla
  // incluidos. Un `div` con onClick se ve igual y no lo alcanza nadie sin ratón.
  const Caja = abrible ? "button" : "div"

  return (
    <>
    <Caja
      type={abrible ? "button" : undefined}
      onClick={abrible ? () => setAbierto(true) : undefined}
      aria-haspopup={abrible ? "dialog" : undefined}
      title={abrible ? t("Ver período a período") : undefined}
      className={cn(
        "group relative block w-full rounded-panel border bg-surface p-3 text-left",
        "shadow-[0_1px_2px_hsl(var(--foreground)/0.04)]",
        "transition-[box-shadow,border-color,transform] duration-200 ease-out",
        "hover:shadow-[0_2px_10px_hsl(var(--foreground)/0.07)]",
        abrible && "cursor-pointer hover:-translate-y-px hover:border-border-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        tono === "destacado" && "border-brand/40",
        tono === "alerta" && "border-warning/45",
      )}
    >
      {detalle && !loading ? (
        <Sparkline valores={detalle.valores} className="absolute right-3 top-3 opacity-70 transition-opacity group-hover:opacity-100" />
      ) : null}
      <div className="eyebrow">{label}</div>

      {value === null ? (
        <div className="mt-1.5">
          {loading ? (
            <span className="text-sm text-muted-foreground">…</span>
          ) : (
            <Badge variant="outline">{badge}</Badge>
          )}
        </div>
      ) : (
        <div
          className={cn(
            "mt-1 text-[1.75rem] leading-[1.15] tracking-[-0.02em]",
            // `.cifra` = num + font-light. La UNICA en rojo es la de atencion.
            tono === "alerta" ? "cifra-atencion" : "cifra",
          )}
        >
          {loading ? "…" : integer ? String(value) : formatAmount(value)}
        </div>
      )}

      {(share !== undefined && share !== 0 && value !== null) || variation != null ? (
        <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-2xs leading-4">
          {share !== undefined && share !== 0 && value !== null ? (
            <span className="text-muted-foreground">
              {formatPercent(value / share, 1)} {shareLabel}
            </span>
          ) : null}
          {variation != null ? (
            <span
              className={cn(
                "inline-flex items-center gap-0.5",
                buenaNoticia ? "text-success" : "text-warning",
              )}
            >
              {sube ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
              {formatPercent(variation, 1)}{" "}{t("vs. período anterior")}</span>
          ) : null}
        </div>
      ) : null}
    </Caja>
    {detalle ? (
      <MetricDetailDialog open={abierto} onOpenChange={setAbierto} titulo={label}
                          detalle={detalle} integer={integer} />
    ) : null}
    </>
  )
}

/**
 * Fila de metricas. Existe para que el ritmo entre tarjetas sea el mismo en las
 * cuatro pantallas y no haya que acordarse del `gap` correcto cada vez.
 *
 * Y para hacer cumplir **«un solo dato en rojo por vista»** (lamina 18). No lo
 * puede resolver sola —cual de dos alertas es LA que importa es una decision de
 * producto— pero avisa en desarrollo, que es donde se puede arreglar. Callarselo
 * seria tener la regla escrita y no tenerla en ningun sitio.
 */
export function MetricRow({ children, className }: { children: ReactNode; className?: string }) {
  if (import.meta.env.DEV) {
    const alertas = Children.toArray(children).filter(
      (c) => isValidElement(c) && (c.props as MetricCardProps).tono === "alerta",
    )
    if (alertas.length > 1) {
      const cuales = alertas
        .map((c) => (isValidElement(c) ? (c.props as MetricCardProps).label : "?"))
        .join(", ")
      // eslint-disable-next-line no-console
      console.error(
        `[brandbook] ${alertas.length} cifras en rojo en la misma fila (${cuales}). ` +
          "La lámina 18 fija una sola por vista: elige cuál requiere atención y " +
          "deja el resto en tono neutro.",
      )
    }
  }
  return (
    <div data-guia="cifras" className={cn("grid gap-2 sm:grid-cols-2 lg:grid-cols-4", className)}>
      {children}
    </div>
  )
}
